import { useCallback } from 'react';
import googleAdsService from '../services/googleAdsService';

/**
 * Custom hook for Google Ads tracking
 * Provides easy access to Google Ads conversion and remarketing tracking methods
 */
export const useGoogleAds = () => {
  // Initialize Google Ads (this will only run once due to service-level check)
  const initializeAds = useCallback(() => {
    googleAdsService.init();
  }, []);

  // Track user registration
  const trackRegistration = useCallback((data = {}) => {
    googleAdsService.trackRegistration(data);
  }, []);

  // Track user login
  const trackLogin = useCallback((data = {}) => {
    googleAdsService.trackLogin(data);
  }, []);

  // Track website analysis
  const trackWebsiteAnalysis = useCallback((data = {}) => {
    googleAdsService.trackWebsiteAnalysis(data);
  }, []);

  // Track website addition
  const trackWebsiteAdded = useCallback((data = {}) => {
    googleAdsService.trackWebsiteAdded(data);
  }, []);

  // Track support ticket creation
  const trackSupportTicket = useCallback((data = {}) => {
    googleAdsService.trackSupportTicket(data);
  }, []);

  // Track purchases
  const trackPurchase = useCallback((value, currency = 'USD', data = {}) => {
    googleAdsService.trackPurchase(value, currency, data);
  }, []);

  // Track subscriptions
  const trackSubscription = useCallback((data = {}) => {
    googleAdsService.trackSubscription(data);
  }, []);

  // Track custom conversions
  const trackConversion = useCallback((conversionLabel, data = {}) => {
    googleAdsService.trackConversion(conversionLabel, data);
  }, []);

  // Track remarketing events
  const trackRemarketingEvent = useCallback((eventName, parameters = {}) => {
    googleAdsService.trackRemarketingEvent(eventName, parameters);
  }, []);

  // Track custom audiences
  const trackCustomAudience = useCallback((audienceName, parameters = {}) => {
    googleAdsService.trackCustomAudience(audienceName, parameters);
  }, []);

  // Track page views for remarketing
  const trackPageView = useCallback((page_location, page_title) => {
    googleAdsService.trackPageView(page_location, page_title);
  }, []);

  // Track enhanced ecommerce events
  const trackEcommerce = useCallback((eventType, eventData = {}) => {
    googleAdsService.trackEcommerce(eventType, eventData);
  }, []);

  return {
    initializeAds,
    trackRegistration,
    trackLogin,
    trackWebsiteAnalysis,
    trackWebsiteAdded,
    trackSupportTicket,
    trackPurchase,
    trackSubscription,
    trackConversion,
    trackRemarketingEvent,
    trackCustomAudience,
    trackPageView,
    trackEcommerce
  };
};
